import { useEffect, useRef } from 'react';
import { GitFork, Loader2 } from 'lucide-react';
import styles from './MessageContextMenu.module.css';

interface MessageContextMenuProps {
  /** Viewport X coordinate (from clientX). */
  x: number;
  /** Viewport Y coordinate (from clientY). */
  y: number;
  /** Called when the user selects "New chat from here". */
  onForkFromHere: () => void;
  /** Called when the menu should close (outside click, Escape). */
  onClose: () => void;
  /** Disables the option while the fork request is in flight. */
  isFetching?: boolean;
}

export function MessageContextMenu({
  x,
  y,
  onForkFromHere,
  onClose,
  isFetching = false,
}: MessageContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onMouse = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouse);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onMouse);
    };
  }, [onClose]);

  const handleFork = () => {
    if (isFetching) return;
    onForkFromHere();
    onClose();
  };

  return (
    <div ref={ref} className={styles.menu} style={{ top: y, left: x }}>
      <button
        className={styles.menuItem}
        onClick={handleFork}
        disabled={isFetching}
        type="button"
      >
        {isFetching ? (
          <Loader2 size={14} className={styles.spinner} />
        ) : (
          <GitFork size={14} />
        )}
        <span>New chat from here</span>
      </button>
    </div>
  );
}
